import { KCAL_PER_GRAM_FAT, KCAL_PER_GRAM_PROTEIN } from "../nutrition/energy";
import { requireFiniteNonNegative } from "../shared/validation";
import type { MacroDistribution } from "./macro-targets";
import { validateMacroDistribution } from "./macro-targets";

/** Versioned because health-policy constants must not silently reinterpret old plans. */
export interface DailyFloorPolicy {
  readonly version: string;
  readonly absoluteMinimumCalories: number;
  readonly minimumNonProteinCalories: number;
  readonly minimumFatGrams: number;
}

/**
 * Conservative product default; kept versioned so future guidance cannot
 * rewrite history. Its evidence boundary is recorded in
 * docs/architecture-decisions/0001-calorie-safety-policy-v1.md.
 */
export const DEFAULT_DAILY_FLOOR_POLICY_V1: DailyFloorPolicy = Object.freeze({
  version: "daily-floor-v1",
  absoluteMinimumCalories: 1_200,
  minimumNonProteinCalories: 400,
  minimumFatGrams: 40,
});

export function calculateDailyCalorieFloor(
  policy: DailyFloorPolicy,
  proteinGrams: number,
  macroDistribution?: MacroDistribution,
): number {
  if (!policy.version.trim()) throw new Error("Daily floor policy version is required");
  requireFiniteNonNegative(policy.absoluteMinimumCalories, "Absolute calorie floor");
  requireFiniteNonNegative(policy.minimumNonProteinCalories, "Minimum non-protein calories");
  requireFiniteNonNegative(policy.minimumFatGrams, "Minimum fat");
  requireFiniteNonNegative(proteinGrams, "Protein target");
  const proteinCalories = proteinGrams * KCAL_PER_GRAM_PROTEIN;
  let ratioCompatibleFatFloor = proteinCalories + policy.minimumFatGrams * KCAL_PER_GRAM_FAT;
  if (macroDistribution) {
    validateMacroDistribution(macroDistribution);
    const remainderTotal = macroDistribution.carbohydratePercent + macroDistribution.fatPercent;
    const fatShareOfRemainder = macroDistribution.fatPercent / remainderTotal;
    if (policy.minimumFatGrams > 0 && fatShareOfRemainder <= 0) {
      throw new Error(`Fat share must be greater than zero to supply the ${policy.minimumFatGrams} g daily minimum`);
    }
    if (fatShareOfRemainder > 0) {
      ratioCompatibleFatFloor = proteinCalories +
        (policy.minimumFatGrams * KCAL_PER_GRAM_FAT) / fatShareOfRemainder;
    }
  }
  return Math.ceil(
    Math.max(
      policy.absoluteMinimumCalories,
      proteinCalories + policy.minimumNonProteinCalories,
      ratioCompatibleFatFloor,
    ),
  );
}
